import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const links = [
  { id: 'hero', label: 'Index' },
  { id: 'zbrush', label: 'Sculpts' },
  { id: 'rts', label: 'Crimson (RTS)' },
  { id: 'rpg-lab', label: 'Asha (RPG)' },
  { id: 'about', label: 'About' },
  { id: 'resume', label: 'Resume' },
  { id: 'contact', label: 'Contact' },
];

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const pickerRef = useRef(null);

  // Track scroll state (backdrop blur)
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Track which section is currently in view (mobile picker label + prev/next)
  useEffect(() => {
    const observers = [];
    links.forEach((l, i) => {
      const el = document.getElementById(l.id);
      if (!el) return;
      const io = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActiveIdx(i);
          });
        },
        { rootMargin: '-40% 0px -55% 0px', threshold: 0 }
      );
      io.observe(el);
      observers.push(io);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, []);

  // Close picker on outside click
  useEffect(() => {
    if (!pickerOpen) return;
    const onDown = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('touchstart', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('touchstart', onDown);
    };
  }, [pickerOpen]);

  const go = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setPickerOpen(false);
  };

  const jump = (delta) => {
    const next = Math.max(0, Math.min(links.length - 1, activeIdx + delta));
    go(links[next].id);
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'backdrop-blur-xl bg-black/70 border-b border-white/10'
          : 'bg-transparent'
      }`}
      data-testid="site-nav"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between gap-3">
        <button
          onClick={() => go('hero')}
          className="font-display text-base sm:text-xl font-black tracking-tight text-white hover:text-[#e58a59] transition-colors whitespace-nowrap"
          data-testid="logo-btn"
        >
          STEPHANOS<span className="text-[#e58a59]">/</span>PAPAS
        </button>

        {/* Desktop nav (unchanged) */}
        <nav className="hidden md:flex items-center gap-1">
          {links.map((l, i) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              className={`px-3 py-2 text-sm uppercase tracking-widest transition-colors relative group ${
                activeIdx === i ? 'text-white' : 'text-zinc-400 hover:text-white'
              }`}
              data-testid={`nav-${l.id}`}
            >
              {l.label}
              <span className={`absolute left-3 right-3 -bottom-0.5 h-px bg-[#e58a59] origin-left transition-transform duration-300 ${
                activeIdx === i ? 'scale-x-100' : 'scale-x-0 group-hover:scale-x-100'
              }`} />
            </button>
          ))}
        </nav>

        <button
          onClick={() => go('contact')}
          className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#e58a59] text-black font-semibold text-sm hover:bg-[#f0a075] transition-colors"
          data-testid="nav-cta-hire"
        >
          Hire Me
        </button>

        {/* Mobile quick-chapter selector — replaces the hamburger */}
        <div className="md:hidden flex items-center gap-1.5" ref={pickerRef}>
          <button
            onClick={() => jump(-1)}
            disabled={activeIdx === 0}
            className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white disabled:opacity-30 disabled:pointer-events-none hover:border-[#e58a59]/50 hover:text-[#e58a59] transition-colors"
            aria-label="Previous section"
            data-testid="mobile-nav-prev"
          >
            <ChevronUp size={16} />
          </button>

          <div className="relative">
            <button
              onClick={() => setPickerOpen((v) => !v)}
              className={`inline-flex items-center gap-2 px-3 h-9 rounded-full border transition-colors ${
                pickerOpen
                  ? 'border-[#e58a59] bg-[#e58a59]/10 text-[#e58a59]'
                  : 'border-white/15 bg-black/40 text-white hover:border-[#e58a59]/50'
              }`}
              aria-label="Jump to section"
              aria-expanded={pickerOpen}
              data-testid="mobile-nav-picker-toggle"
            >
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#e58a59] font-bold">
                {String(activeIdx + 1).padStart(2, '0')}
              </span>
              <span className="text-xs font-semibold whitespace-nowrap max-w-[110px] truncate">
                {links[activeIdx].label}
              </span>
              <ChevronsUpDown size={13} className="opacity-70" />
            </button>

            <AnimatePresence>
              {pickerOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -8, scale: 0.98 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -8, scale: 0.98 }}
                  transition={{ duration: 0.18 }}
                  className="absolute right-0 mt-2 w-56 rounded-xl border border-white/10 bg-black/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                  data-testid="mobile-nav-picker"
                >
                  <div className="px-3 py-2 border-b border-white/5">
                    <div className="text-[9px] uppercase tracking-[0.28em] text-[#e58a59] font-bold">
                      Jump to Chapter
                    </div>
                  </div>
                  <div className="py-1 max-h-[70vh] overflow-y-auto">
                    {links.map((l, i) => {
                      const isActive = activeIdx === i;
                      return (
                        <button
                          key={l.id}
                          onClick={() => go(l.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                            isActive
                              ? 'bg-[#e58a59]/10 text-[#e58a59]'
                              : 'text-zinc-200 hover:bg-white/5'
                          }`}
                          data-testid={`nav-mobile-${l.id}`}
                        >
                          <span className={`text-[10px] font-bold w-5 shrink-0 ${isActive ? 'text-[#e58a59]' : 'text-zinc-500'}`}>
                            {String(i + 1).padStart(2, '0')}
                          </span>
                          <span className="text-sm font-medium truncate flex-1">
                            {l.label}
                          </span>
                          {isActive && <span className="w-1.5 h-1.5 rounded-full bg-[#e58a59]" />}
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <button
            onClick={() => jump(1)}
            disabled={activeIdx === links.length - 1}
            className="w-9 h-9 rounded-full border border-white/15 flex items-center justify-center text-white disabled:opacity-30 disabled:pointer-events-none hover:border-[#e58a59]/50 hover:text-[#e58a59] transition-colors"
            aria-label="Next section"
            data-testid="mobile-nav-next"
          >
            <ChevronDown size={16} />
          </button>
        </div>
      </div>
    </motion.header>
  );
}
